<template>
  <v-card elevation="2" :loading="loading" v-if="subscription">
    <v-card-title>End an active subscription</v-card-title>
    <v-card-text>
      <v-autocomplete
        v-if="subscription.billingOrder"
        v-model="form.variant"
        dense
        label="Transition to product variant"
        :items="productList"
      ></v-autocomplete>
      <v-autocomplete
        v-model="form.reason"
        dense
        label="Cancellation Reason"
        :items="cancellationReasonsList"
      ></v-autocomplete>
      <v-text-field v-model="form.comment" label="Comment"></v-text-field>
      <p>
        Paid up until:
        {{
          subscription.billingOrder && subscription.billingOrder.nextBillingDate
        }} <small>Payments due after the 28th are billed on the 28th</small>
      </p>
      <p>Service start date: {{ subscription.serviceStartDate }}</p>
      <v-switch
        v-if="subscription.billingOrder"
        inset
        class="py-0 my-0"
        :input-value="form.customDate"
        @change="form.customDate = !form.customDate"
      >
        <template v-slot:label>
          <b class="pr-2">Set custom date</b>
        </template>
      </v-switch>
      <v-menu
        v-if="form.customDate"
        v-model="menu"
        :close-on-content-click="false"
        :nudge-right="40"
        transition="scale-transition"
        offset-y
        min-width="290px"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            v-model="form.requiredDate"
            label="End of Billing Cycle"
            prepend-icon="mdi-calendar"
            readonly
            v-bind="attrs"
            v-on="on"
          ></v-text-field>
        </template>
        <v-date-picker
          v-model="form.requiredDate"
          @input="menu = false"
          :allowed-dates="(val) => new Date(val) > new Date()"
        ></v-date-picker>
      </v-menu>
    </v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn
        color="secondary"
        :disabled="!form.requiredDate || subscription.stage == 'FINISHED'"
        :loading="saving"
        rounded
        @click="endSubscription()"
        >End subscription</v-btn
      >
    </v-card-actions>
  </v-card>
</template>

<script>
import {
  CANCELLATION_REASONS_QUERY,
  SUBSCRIPTION_QUERY,
  END_SUBSCRIPTION_MUTATION,
  BILLING_PRODUCTS_QUERY,
} from "@/constants/graphql/users/subscription";
import { YYYYMMDD_readable } from "@/lib/fancy_date";
import { getUserName } from "@/lib/username";

export default {
  name: "EndSubscription",
  props: ["subscriptionId"],
  data() {
    return {
      loading: true,
      saving: false,
      subscription: null,
      cancellationReasonsList: [],
      productList: [],
      menu: false,
      form: {
        requiredDate: null,
        reason: null,
        comment: null,
        variant: null,
        customDate: false,
      },
    };
  },
  methods: {
    getName(user) {
      return getUserName(user);
    },
    formatDate(date) {
      return YYYYMMDD_readable(date);
    },
    getId(base64string) {
      let decoded = atob(base64string).split(":");
      if (decoded[1]) return decoded[1];
      return "";
    },
    endSubscription() {
      this.saving = true;
      let input = {
        id: this.subscription.id,
        endDate: this.form.requiredDate,
        cancellationReasonId: this.form.reason,
        transitionProductVariantId: this.form.variant,
        comment: this.form.comment,
      };

      this.$apollo
        .mutate({
          mutation: END_SUBSCRIPTION_MUTATION,
          variables: {
            input: input,
          },
          refetchQueries: ["subscriptionQuery"],
        })
        .then((result) => {
          if (!result.data.processSubscriptionCancellation.success) {
            alert(result.data.processSubscriptionCancellation.error);
          } else {
            this.$toasted.success("Subscription has been updated", {
              icon: "arm-flex",
              duration: 2000,
              position: "bottom-center",
            });
          }
          this.saving = false;
        })
        .catch((_error) => {
          this.saving = false;
        });
    },
  },
  apollo: {
    subscription: {
      query: SUBSCRIPTION_QUERY,
      variables() {
        return {
          id: this.subscriptionId,
        };
      },
      fetchPolicy: "cache-and-network",
      update(data) {
        return data.subscriptionById;
      },
      result(response) {
        if (response.data) {
          this.loading = false;
          let subscription = response.data.subscriptionById;
          if (subscription.subscriptioncancellationreasonSet.length > 0) {
            this.form.reason =
              subscription.subscriptioncancellationreasonSet[0].cancellationReason.id;
            this.form.comment =
              subscription.subscriptioncancellationreasonSet[0].comment;
          }
          if (subscription.billingOrder == null) {
            this.form.customDate = true;
            this.form.requiredDate = subscription.endDate;
          } else {
            // if end date is set show date picker
            if (subscription.endDate) {
              this.form.customDate = true;
            }
            if (subscription.billingOrder) {
              this.form.requiredDate = subscription.endDate
                ? this.$moment(subscription.endDate).format("YYYY-MM-DD")
                : this.$moment(
                    subscription.billingOrder.nextBillingDate
                  ).format("YYYY-MM-DD");
            }
          }
          if (subscription.transitionProductVariant) {
            this.form.variant = this.getId(
              subscription.transitionProductVariant.id
            );
          }
          if (subscription.endDate) {
            this.form.requiredDate = this.$moment(subscription.endDate).format(
              "YYYY-MM-DD"
            );
          }
        }
      },
    },
    cancellationReasons: {
      query: CANCELLATION_REASONS_QUERY,
      fetchPolicy: "cache-and-network",
      result(response) {
        if (response.data && !response.loading) {
          this.cancellationReasonsList = [];

          response.data.cancellationReasons.forEach((item) => {
            let data = {
              value: item.id,
              text: item.reason,
            };

            this.cancellationReasonsList.push(data);
          });
        }
        if (
          response.error &&
          response.error.gqlError?.message == "Permission Denied."
        ) {
          this.$router.push("/no-access");
        }
      },
    },
    billingProducts: {
      query: BILLING_PRODUCTS_QUERY,
      fetchPolicy: "cache-and-network",
      result(response) {
        if (response.data && !response.loading) {
          this.productList = [{ value: null, text: "Does not transtion" }];

          response.data.billingProducts.edges.forEach((item) => {
            if (!item.node.requiresActiveSubscription) {
              item.node.productvariantSet.edges.forEach((item) => {
                if (item.node.product.productCategory) {
                  let data = {
                    value: this.getId(item.node.id),
                    text: `${item.node.product.title} ${item.node.billingPeriod}`,
                  };

                  this.productList.push(data);
                }
              });
            }
          });
        }
        if (
          response.error &&
          response.error.gqlError?.message == "Permission Denied."
        ) {
          this.$router.push("/no-access");
        }
      },
    },
  },
};
</script>
